import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';

const spacerMargin = (theme: Theme) => ({
  marginTop: theme.spacing(0.75),
});

const iconButtonNoRippleInteraction = (theme: Theme) => ({
  ...theme.pointers.hover({
    // background: theme.colors.backgroundLighter,
  }),
  '&.Mui-focusVisible': {
    // background: theme.colors.backgroundLighter,
  },
});
const iconButtonSmallIcon = (theme: Theme) => ({
  '& svg': {
    fontSize: theme.typography.pxToRem(16),
  },
});

const truncateSingleLine = () => ({
  '& .MuiTypography-root': {
    display: 'block',
    overflowX: 'hidden',
    overflowY: 'visible',
    outline: 'none',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
});

const listItemPositioning = (theme: Theme) => ({
  paddingBottom: theme.spacing(1.75),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1.25),
  paddingTop: theme.spacing(1.75),
  '& svg': {
    fontSize: theme.spacing(3),
  },
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2.25),
  },
  '& .MuiListItemIcon-root': {
    minWidth: leftInset(theme),
  },
  '& .MuiListItemIcon-root:last-child': {
    justifyContent: 'flex-end',
  },
  '& .MuiListItemText-multiline, & .MuiListItemText-root': {
    margin: 0,
  },
});

const leftInset = (theme: Theme) => theme.spacing(5);

export const useBaseStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: theme.colors.bgLight,
      flexGrow: 1,
    },
  })
);

export const useButtonStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      border: `1px solid ${theme.colors.borderDefault}`,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      '& .MuiButton-label': {
        fontWeight: theme.typography.fontWeightBold,
        '& .button-overflow': {
          maxWidth: 140,
          minWidth: 0,
          overflow: 'hidden',
          outline: 'none',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          [theme.breakpoints.up('sm')]: {
            maxWidth: 180,
          },
        },
      },
      [theme.breakpoints.up('sm')]: {
        border: 'none',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: 16,
      },
    },
  })
);

export const useCurrentPositionListItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      color: theme.colors.fgDefault,
      background: theme.colors.bgDefault,
      transition: `${theme.transitions.duration.short}ms color ${theme.transitions.easing.easeInOut}`,
      position: 'relative',
      '& .MuiListItemIcon-root, & .MuiTypography-colorTextPrimary, & .MuiTypography-colorTextSecondary':
        {
          color: 'inherit',
        },
      '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
        ...truncateSingleLine(),
      },
      minHeight: theme.spacing(9),
      ...listItemPositioning(theme),
      ...spacerMargin(theme),
      ...theme.pointers.hover({
        backgroundColor: theme.colors.bgDefault,
        color: theme.colors.fgPrimaryBase,
      }),
      '&$disabled': {
        color: theme.colors.fgDisabled,
        backgroundColor: theme.colors.bgLight,
        opacity: 1,
      },
      '&$focusVisible': {
        backgroundColor: theme.colors.bgDefault,
        boxShadow: `inset 0 0 0 3px ${theme.colors.borderPrimaryBase}`,
        color: theme.colors.fgPrimaryBase,
        position: 'relative',
        zIndex: 1,
      },
    },
    disabled: {},
    focusVisible: {},
  })
);

export const useDialogPaperStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: theme.spacing(65),
    },
  })
);

export const useHeadingStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginLeft: theme.spacing(3),
      marginTop: theme.spacing(3),
      padding: 0,
    },
  })
);

export const useHistoryListItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      borderBottom: `1px solid ${theme.colors.borderDefault}`,
      color: theme.colors.fgDefault,
      transition: `${theme.transitions.duration.short}ms background-color ${theme.transitions.easing.easeInOut}`,
      '& .MuiListItemIcon-root, & .MuiTypography-colorTextPrimary': {
        color: 'inherit',
      },
      '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
        ...truncateSingleLine(),
      },
      '& .MuiTypography-colorTextSecondary': {
        color: theme.colors.fgSubtle,
      },
      '& .MuiIconButton-root': {
        opacity: 0,
        transition: `${theme.transitions.duration.short}ms opacity ${theme.transitions.easing.easeInOut}`,
        ...iconButtonNoRippleInteraction(theme),
        ...iconButtonSmallIcon(theme),
      },
      minHeight: theme.spacing(11.5),
      ...listItemPositioning(theme),
      ...theme.pointers.hover({
        backgroundColor: theme.colors.bgDefault, // Formerly oat20
        '& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary': {
          color: theme.colors.fgPrimaryBase,
        },
        '& .MuiIconButton-root': {
          opacity: 1,
        },
      }),
      ...theme.pointers.noHover({
        '& .MuiIconButton-root': {
          opacity: 1,
        },
      }),
      '&$focusVisible': {
        backgroundColor: theme.colors.bgLight, // Formerly oat20
        boxShadow: `inset 0 0 0 3px ${theme.colors.borderPrimaryBase}`,
        '& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary': {
          color: theme.colors.fgPrimaryBase,
        },
        '& .MuiIconButton-root': {
          opacity: 1,
        },
      },
    },
    focusVisible: {},
  })
);

export const useSearchInputStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)':
        {
          margin: 0,
        },
      '& .MuiInputLabel-root:not(.Mui-focused):not(.MuiFormLabel-filled)': {
        transform: `translate(${theme.spacing(7)}px, 34px) scale(1) !important`,
        [theme.breakpoints.up('sm')]: {
          transform: `translate(${theme.spacing(8)}px, 34px) scale(1) !important`,
        },
      },
      '& .MuiInputLabel-root.MuiFormLabel-filled, & .MuiInputLabel-root.Mui-focused': {
        transform: `translate(${theme.spacing(7)}px, 24px) scale(0.75) !important`,
        [theme.breakpoints.up('sm')]: {
          transform: `translate(${theme.spacing(8)}px, 22px) scale(0.75) !important`,
        },
      },
    },
  };
});

export const useSearchInputInputStyles = makeStyles((theme: Theme) => {
  const totalInputPadding = theme.spacing(2);
  return createStyles({
    adornedStart: {},
    root: {
      background: theme.colors.bgDefault,
      borderRadius: 0,
      minHeight: theme.spacing(9),
      paddingBottom: totalInputPadding - theme.spacing(1.5),
      paddingLeft: `${totalInputPadding}px !important`,
      paddingRight: `${theme.spacing(1.25)}px !important`,
      paddingTop: totalInputPadding - theme.spacing(1.5),
      [theme.breakpoints.up('sm')]: {
        paddingLeft: `${theme.spacing(3)}px !important`,
        paddingRight: `${theme.spacing(2.25)}px !important`,
      },
      ...spacerMargin(theme),
      ...theme.pointers.hover({
        background: theme.colors.bgDefault,
      }),
      '& .MuiInputBase-input': {
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        paddingRight: 0,
        paddingTop: theme.spacing(2.5),
        [theme.breakpoints.up('sm')]: {
          paddingTop: theme.spacing(2),
        },
      },
      '& svg': {
        fontSize: theme.spacing(3),
      },
      '&$focused': {
        background: theme.colors.bgDefault,
        '& .MuiInputAdornment-positionStart': {
          color: theme.colors.fgPrimaryBase,
        },
      },
      '& .MuiIconButton-root': {
        ...iconButtonNoRippleInteraction(theme),
        ...iconButtonSmallIcon(theme),
      },
    },
    focused: {},
  });
});

export const useSearchListItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: theme.colors.bgDefault,
      borderRadius: 0,
      borderTop: `1px solid ${theme.colors.borderDefault}`,
      paddingBottom: theme.spacing(2.5),
      paddingLeft: theme.spacing(2) + leftInset(theme),
      paddingRight: theme.spacing(3.5),
      paddingTop: theme.spacing(2.5),
      [theme.breakpoints.up('sm')]: {
        paddingLeft: theme.spacing(3) + leftInset(theme),
      },
      '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
        ...truncateSingleLine(),
      },
      '& .MuiListItemText-root': {
        margin: 0,
      },
      ...theme.pointers.hover({
        background: theme.colors.bgDefault,
        '& .MuiListItemText-primary .MuiTypography-root, & .MuiListItemText-secondary .MuiTypography-root':
          {
            color: theme.colors.fgPrimaryBase,
          },
      }),
      '&$focusVisible': {
        background: theme.colors.bgDefault,
        boxShadow: `inset 0 0 0 3px ${theme.colors.borderPrimaryBase}`,
        '& .MuiListItemText-primary .MuiTypography-root, & .MuiListItemText-secondary .MuiTypography-root':
          {
            color: theme.colors.fgPrimaryBase,
          },
      },
      '&$selected': {
        background: theme.colors.bgLight,
      },
    },
    selected: {},
    focusVisible: {},
  })
);

export const useListStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
  })
);
